import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, headers: { 'Content-Type': 'application/json' }, withCredentials: true });

export const AuthAPI = {
  exchange: (sessionId) => client.post('/auth/session', { session_id: sessionId }).then((r) => r.data),
  login: (email, password) => client.post('/auth/login', { email, password }).then((r) => r.data),
  me: () => client.get('/auth/me').then((r) => r.data),
  logout: () => client.post('/auth/logout').then((r) => r.data),
};

export const AssetsAPI = {
  list: (params = {}) => client.get('/assets', { params }).then((r) => r.data),
  get: (id) => client.get(`/assets/${id}`).then((r) => r.data),
  create: (payload) => client.post('/assets', payload).then((r) => r.data),
  update: (id, payload) => client.patch(`/assets/${id}`, payload).then((r) => r.data),
  remove: (id) => client.delete(`/assets/${id}`).then((r) => r.data),
  restore: (id) => client.post(`/assets/${id}/restore`).then((r) => r.data),
  permanentDelete: (id) => client.delete(`/assets/${id}/permanent`).then((r) => r.data),
  emptyTrash: () => client.delete('/trash').then((r) => r.data),
  duplicate: (id) => client.post(`/assets/${id}/duplicate`).then((r) => r.data),
};

export const PublicAPI = {
  getForm: (shareId) => client.get(`/public/forms/${shareId}`).then((r) => r.data),
  submit: (shareId, data) => client.post(`/public/forms/${shareId}/submit`, { data }).then((r) => r.data),
};

export const SubmissionsAPI = {
  list: (assetId) => client.get(`/assets/${assetId}/submissions`).then((r) => r.data),
  create: (assetId, data) => client.post(`/assets/${assetId}/submissions`, { data }).then((r) => r.data),
  remove: (assetId, subId) => client.delete(`/assets/${assetId}/submissions/${subId}`).then((r) => r.data),
};

export const AssistantAPI = {
  chat: (message, sessionId) => client.post('/assistant/chat', { message, session_id: sessionId }).then((r) => r.data),
};

export const LabelsAPI = {
  list: () => client.get('/labels').then((r) => r.data),
  create: (payload) => client.post('/labels', payload).then((r) => r.data),
  update: (id, payload) => client.patch(`/labels/${id}`, payload).then((r) => r.data),
  remove: (id) => client.delete(`/labels/${id}`).then((r) => r.data),
  addToAsset: (assetId, labelId) => client.post(`/assets/${assetId}/labels/${labelId}`).then((r) => r.data),
  removeFromAsset: (assetId, labelId) => client.delete(`/assets/${assetId}/labels/${labelId}`).then((r) => r.data),
};

export const TeamsAPI = {
  list: () => client.get('/teams').then((r) => r.data),
  create: (payload) => client.post('/teams', payload).then((r) => r.data),
  invite: (id, email, role) => client.post(`/teams/${id}/invite`, { email, role }).then((r) => r.data),
  removeMember: (id, email) => client.delete(`/teams/${id}/members/${encodeURIComponent(email)}`).then((r) => r.data),
  resendInvite: (id, email) => client.post(`/teams/${id}/members/${encodeURIComponent(email)}/resend`).then((r) => r.data),
  remove: (id) => client.delete(`/teams/${id}`).then((r) => r.data),
};

export const AIAPI = {
  generateForm: (prompt, teamId) => client.post('/ai/generate-form', { prompt, team_id: teamId }).then((r) => r.data),
};

export const OnboardingAPI = {
  challenge: () => client.get('/onboarding/challenge').then((r) => r.data),
  validate: (data, fields) => client.post('/onboarding/validate', { data, fields }).then((r) => r.data),
  submit: (data, verification) => client.post('/onboarding/submit', { data, ...verification }).then((r) => r.data),
  saveDraft: (payload) => client.post('/onboarding/draft', payload).then((r) => r.data),
  submissions: (params = {}) => client.get('/onboarding/submissions', { params }).then((r) => r.data),
  submission: (id) => client.get(`/onboarding/submissions/${id}`).then((r) => r.data),
  remove: (id) => client.delete(`/onboarding/submissions/${id}`).then((r) => r.data),
  drafts: () => client.get('/onboarding/drafts').then((r) => r.data),
  remindDraft: (id) => client.post(`/onboarding/drafts/${id}/remind`).then((r) => r.data),
  removeDraft: (id) => client.delete(`/onboarding/drafts/${id}`).then((r) => r.data),
  signatureUrl: (id) => `${API}/onboarding/submissions/${id}/signature.png`,
  pdfUrl: (id) => `${API}/onboarding/submissions/${id}/pdf`,
  csvUrl: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return `${API}/onboarding/submissions.csv${qs ? `?${qs}` : ''}`;
  },
};

export const AdminAPI = {
  users: () => client.get('/admin/users').then((r) => r.data),
  faqs: () => client.get('/admin/faqs').then((r) => r.data),
  createFaq: (question, answer) => client.post('/admin/faqs', { question, answer }).then((r) => r.data),
  updateFaq: (id, question, answer) => client.put(`/admin/faqs/${id}`, { question, answer }).then((r) => r.data),
  deleteFaq: (id) => client.delete(`/admin/faqs/${id}`).then((r) => r.data),
  emailDomain: () => client.get('/admin/email-domain').then((r) => r.data),
  verifyEmailDomain: () => client.post('/admin/email-domain/verify').then((r) => r.data),
  addEmailDomain: () => client.post('/admin/email-domain/add').then((r) => r.data),
  stats: (weeks = 8) => client.get('/admin/stats', { params: { weeks } }).then((r) => r.data),
  activity: (params = {}) => client.get('/admin/activity', { params }).then((r) => r.data),
  admins: () => client.get('/admin/admins').then((r) => r.data),
  addAdmin: (email) => client.post('/admin/admins', { email }).then((r) => r.data),
  removeAdmin: (email) => client.delete(`/admin/admins/${encodeURIComponent(email)}`).then((r) => r.data),
};

export const SupportAPI = {
  myMessages: () => client.get('/support/messages').then((r) => r.data),
  send: (body) => client.post('/support/messages', { body }).then((r) => r.data),
  escalate: () => client.post('/support/escalate').then((r) => r.data),
  conversations: () => client.get('/support/conversations').then((r) => r.data),
  conversation: (id) => client.get(`/support/conversations/${id}`).then((r) => r.data),
  reply: (id, body) => client.post(`/support/conversations/${id}/reply`, { body }).then((r) => r.data),
  customer: (id) => client.get(`/support/conversations/${id}/customer`).then((r) => r.data),
  cannedReplies: () => client.get('/support/canned-replies').then((r) => r.data),
  createCanned: (title, body) => client.post('/support/canned-replies', { title, body }).then((r) => r.data),
  updateCanned: (id, title, body) => client.put(`/support/canned-replies/${id}`, { title, body }).then((r) => r.data),
  deleteCanned: (id) => client.delete(`/support/canned-replies/${id}`).then((r) => r.data),
  markCannedUsed: (id) => client.post(`/support/canned-replies/${id}/used`).then((r) => r.data),
};

export const AccountAPI = {
  changePassword: (current_password, new_password) =>
    client.post('/auth/change-password', { current_password, new_password }).then((r) => r.data),
};

export default client;
